/**
 * System-generated human-facing Demand IDs.
 * Format: DJ-YYYY-###### (e.g. DJ-2026-000184)
 * ROWID remains the internal primary key for APIs and routing.
 */

export const DEMAND_ID_PREFIX = 'DJ';
export const DEMAND_ID_PATTERN = /^DJ-(\d{4})-(\d{6})$/;

const LOCAL_SEQ_PREFIX = 'demandjet.demandIdSeq.';

export function demandIdYear(date = new Date()) {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return String(new Date().getUTCFullYear());
  return String(value.getUTCFullYear());
}

export function formatDemandId(year, sequence) {
  const y = String(year || '').trim();
  const seq = Math.max(1, Math.floor(Number(sequence) || 0));
  return `${DEMAND_ID_PREFIX}-${y}-${String(seq).padStart(6, '0')}`;
}

export function isValidDemandId(value) {
  return DEMAND_ID_PATTERN.test(String(value || '').trim());
}

export function parseDemandId(value) {
  const match = String(value || '').trim().match(DEMAND_ID_PATTERN);
  if (!match) return null;
  return {
    year: match[1],
    sequence: Number(match[2]),
    formatted: match[0],
  };
}

/** Demand ID on a matter row, or null when not assigned yet. */
export function matterDemandId(matter = {}) {
  const raw = String(matter?.Demand_ID || '').trim();
  return isValidDemandId(raw) ? raw : null;
}

/**
 * Prefer Demand ID, then invoice ID, then ROWID for human references.
 */
export function matterDisplayRef(matter = {}) {
  return (
    matterDemandId(matter) ||
    String(matter?.External_Invoice_ID || matter?.Invoice_ID || '').trim() ||
    String(matter?.ROWID || '').trim() ||
    '—'
  );
}

function localSeqKey(year) {
  return `${LOCAL_SEQ_PREFIX}${year}`;
}

function readLocalSeq(year) {
  try {
    if (typeof localStorage === 'undefined') return 0;
    const n = Number(localStorage.getItem(localSeqKey(year)) || 0);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

function writeLocalSeq(year, sequence) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(localSeqKey(year), String(Math.max(0, Math.floor(sequence))));
  } catch {
    // Private mode / quota — in-memory callers still get a formatted id.
  }
}

/** Raise the local counter when we see an existing Demand ID (demo seeds, sync). */
export function noteDemandIdAllocated(demandId) {
  const parsed = parseDemandId(demandId);
  if (!parsed) return;
  const current = readLocalSeq(parsed.year);
  if (parsed.sequence > current) writeLocalSeq(parsed.year, parsed.sequence);
}

/** Allocate the next Demand ID in the browser (offline / demo creates). */
export function allocateLocalDemandId(now = new Date()) {
  const year = demandIdYear(now);
  const next = readLocalSeq(year) + 1;
  writeLocalSeq(year, next);
  return formatDemandId(year, next);
}

/**
 * Resolve a Demand ID for create payloads:
 * prefer a valid client-provided id, otherwise allocate locally.
 */
export function resolveCreateDemandId(candidate, now = new Date()) {
  const raw = String(candidate || '').trim();
  if (isValidDemandId(raw)) {
    noteDemandIdAllocated(raw);
    return raw;
  }
  return allocateLocalDemandId(now);
}

/** @internal test helper */
export function __resetLocalDemandIdSeq(year) {
  const y = year || demandIdYear();
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(localSeqKey(y));
  } catch {
    // ignore
  }
}
